import {Component, OnInit, OnDestroy} from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from 'src/app/app.service';
import { SiteModel } from 'src/app/models/site.model';
import { ApiService } from 'src/app/shared/api.service';
import { UtilsService, GooglePlacePrediction } from 'src/app/shared/utils.service';
import moment, {Moment} from 'moment';
import {SitesSelectorComponent} from "../../shared/sites-selector/sites-selector.component";
import {TagsSelectorComponent} from "../../shared/tags-selector/tags-selector.component";
import {Subject} from "rxjs";
import {debounceTime, takeUntil} from "rxjs/operators";

declare var google;

@Component({
  selector: 'app-sites-details-edit',
  templateUrl: './sites-details-edit.component.html',
  styleUrls: ['./sites-details-edit.component.scss']
})
export class SitesDetailsEditComponent implements OnInit, OnDestroy {
  /**
   * Is the form being submitted?
   */
  inProgress = false;

  // the site model placeholder
  site: SiteModel = new SiteModel();

  site_risk_assessments: string[] = [
    'very low',
    'low',
    'moderate',
    'high',
    'critical'
  ];

  estimatedProjectStartDate: moment.Moment | null;
  estimatedProjectEndDate: moment.Moment | null;
  siteOpenTime: moment.Moment | null;
  siteCloseTime: moment.Moment | null;

  // Address lookup (Google Places Data API).
  addressPredictions: GooglePlacePrediction[] = [];
  private addressInput$: Subject<string> = new Subject<string>();
  private placesSessionToken: any = null;
  private destroy$: Subject<void> = new Subject<void>();

  /**
   * Site statuses and site types are pulled from the utils service file.
   */

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public app: AppService,
    private api: ApiService,
    public utils: UtilsService
  ) { }

  ngOnInit() {
    // Get the site id from the route params.
    const parent_id: number = Number(this.route.parent.snapshot.params['parent_id']);
    const child_id: number = Number(this.route.parent.snapshot.params['child_id']);

    // Check if we are updating a site or creating a new one.
    if ( child_id || (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' && parent_id) ) {
      // Store the site id.
      this.site.id = child_id ? child_id : parent_id;
      // Get the data from the API.
      this.api.laravelApiRequest(
        'get',
        'sites/' + this.site.id,
        {},
        {},
        (response) => {
          // apply the data
          this.site.apply(response.data);

          // Set the estimated project start date
          if (this.site.estimated_project_start_date) {
            this.estimatedProjectStartDate = moment.unix(this.site.estimated_project_start_date);
          }

          // Set the estimated project end date
          if (this.site.estimated_project_end_date) {
            this.estimatedProjectEndDate = moment.unix(this.site.estimated_project_end_date);
          }

          // Set the site opening time
          if (this.site.open_time) {
            this.siteOpenTime = this.toMoment(this.site.open_time);
          }

          // Set the site closing time
          if (this.site.close_time) {
            this.siteCloseTime = this.toMoment(this.site.close_time);
          }

          // Extract Tag ids.
          this.site.tag_ids = this.site.tags ? this.site.tags.map((tag) => tag.id) : [];
        },
        (error) => {
          this.utils.showModal('Error', error.message);
        }
      );
    } else {
      // Get and store the parent site id if it is present.
      if ( parent_id ) {
        // Store the site id.
        this.site.parent_site_id = parent_id;
      }

      // Set the site opening time
      if (this.site.open_time) {
        this.siteOpenTime = this.toMoment(this.site.open_time);
      }

      // Set the site closing time
      if (this.site.close_time) {
        this.siteCloseTime = this.toMoment(this.site.close_time);
      }

      // Set up the address lookup (Google Places Data API). No map here, so predictions are
      // region-biased to the configured default country only.
      this.setupAddressLookup();
    }
  }

  // get timezone output from timezone select component
  getOutputTimezone($event) {
    this.site.timezone = $event;
  }

  /**
   * Clean up subscriptions when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Wire up the address lookup using the Google Places Data API. Debounced query changes fetch
   * predictions which are shown in the Material autocomplete panel. Selecting a prediction resolves
   * the full formatted address.
   * @private
   */
  private setupAddressLookup(): void {
    this.addressInput$.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe((value: string): void => {
      if (!value || value.trim().length < 3) {
        this.addressPredictions = [];
        return;
      }

      // Start a session on first keystroke of a new search.
      if (!this.placesSessionToken) {
        this.utils.createPlacesSessionToken()
          .then((token: any): void => {
            this.placesSessionToken = token;
          })
          .catch((): void => { /* token is optional; predictions still work without it */ });
      }

      this.utils.getPlacePredictions(value, this.placesSessionToken)
        .then((predictions: GooglePlacePrediction[]): void => {
          this.addressPredictions = predictions;
        })
        .catch((): void => {
          this.addressPredictions = [];
        });
    });
  }

  /**
   * Called as the user types in the address field. Keeps the model in sync (a free-text address is
   * still valid) and pushes the query into the debounced prediction stream.
   * @param value The current input value.
   */
  onAddressInputChange(value: string): void {
    this.site.location = value || '';
    this.addressInput$.next(value || '');
  }

  /**
   * Handle selection of an address prediction: resolve the place details and store the formatted
   * address.
   * @param prediction The selected prediction.
   */
  onSelectPrediction(prediction: GooglePlacePrediction): void {
    this.utils.getPlaceDetails(prediction.placePrediction)
      .then((details): void => {
        this.site.location = details.formattedAddress;
        this.addressPredictions = [];
        // The details fetch ends the billing session; start a fresh one for the next search.
        this.placesSessionToken = null;
      })
      .catch((): void => { /* ignore: user can still type a free-text address */ });
  }

  /**
   * Create or update the site.
   * @param form
   */
  onSubmit(form: NgForm) {
    // make sure the form data is valid
    if (form.valid) {
      if (this.inProgress) {
        return;
      }

      this.inProgress = true;

      // Convert the estimated project start and end dates to a unix timestamps.
      if (this.estimatedProjectStartDate && this.estimatedProjectStartDate instanceof moment) {
        this.site.estimated_project_start_date = this.estimatedProjectStartDate.unix();
      } else {
        this.site.estimated_project_start_date = null;
      }

      if (this.estimatedProjectEndDate && this.estimatedProjectEndDate instanceof moment) {
        this.site.estimated_project_end_date = this.estimatedProjectEndDate.unix();
      } else {
        this.site.estimated_project_end_date = null;
      }

      // Convert the opening and closing time to a time string.
      if (this.siteOpenTime && this.siteOpenTime instanceof moment) {
        this.site.open_time = this.toTimeString(this.siteOpenTime);
      }

      if (this.siteCloseTime && this.siteCloseTime instanceof moment) {
        this.site.close_time = this.toTimeString(this.siteCloseTime);
      }

      // Check if the site should be created or updated.
      if (this.site.id) {
        this.api.laravelApiRequest(
          'put',
          'sites/' + this.site.id,
          this.site,
          {},
          (response) => {
            this.utils.showToast('The ' + this.utils.getLangTerm(this.site.parent_site_id ? 'child-sites.singular' : 'sites.singular', this.site.parent_site_id ? 'Subsite' : 'Site').toLowerCase() + ' was updated.');
            this.inProgress = false;
          },
          () => this.inProgress = false
        );
      } else {
        // Create a new site.
        this.api.laravelApiRequest(
          'post',
          'sites',
          this.site,
          {},
          (response) => {
            this.utils.showToast('The ' + this.utils.getLangTerm(this.site.parent_site_id ? 'child-sites.singular' : 'sites.singular', this.site.parent_site_id ? 'Subsite' : 'Site').toLowerCase() + ' was added.');
            this.inProgress = false;

            // Refresh the component to invoke change detection for the new route.
            this.router
              .navigateByUrl('/RefreshComponent', { skipLocationChange: true })
              .then(() => {
                this.router.navigate([
                  '/sites',
                  response.data.id,
                  'edit',
                  'details'
                ]);
              });
          },
          () => this.inProgress = false
        );
      }
    } else {
      this.utils.showModal('Form Error', 'Please make sure you enter all fields with valid information.');
    }
  }

  /**
   * Remove a single site.
   */
  onRemove() {
    this.utils.showModal(
      'Remove ' + this.utils.getLangTerm(this.site.parent_site_id ? 'child-sites.singular' : 'sites.singular', this.site.parent_site_id ? 'Subsite' : 'Site'),
      'Are you sure you want to remove this ' + this.utils.getLangTerm(this.site.parent_site_id ? 'child-sites.singular' : 'sites.singular', this.site.parent_site_id ? 'Subsite' : 'Site').toLowerCase() + '?',
      () => {
        this.api.laravelApiRequest(
          'delete',
          'sites/' + this.site.id,
          {},
          {},
          (response) => {
            this.utils.showToast('The ' + this.utils.getLangTerm(this.site.parent_site_id ? 'child-sites.singular' : 'sites.singular', this.site.parent_site_id ? 'Subsite' : 'Site').toLowerCase() + ' was removed.');
            this.router.navigate(['/sites']);
          },
          (error) => {
            this.utils.showModal('Error', error.message);
          }
        );
      }
    );
  }

  /**
   * Show the site start and end dates if the status of the site is any of:
   * - Pre-Construction
   * - Under Construction
   * - Hand Over Complete
   *
   * @param site_status The status of the site.
   */
  showProjectStartEndDates(site_status: string) {
    return [
      'Pre-Construction',
      'Under Construction',
      'Hand Over Complete'
    ].indexOf(site_status) > -1;
  }

  /**
   * Added a new site selector to link sites with parent sites.
   */
  onSelectParentSite() {
    this.utils.showComponentDialog(
      SitesSelectorComponent,
      {
        selected: [this.site.parent_site_id],
        multiple: false,
        allow_no_selection: true
      },
      {
        width: '1200px'
      },
      (results) => {
        if ( typeof results !== 'undefined' ) {
          this.site.parent_site_id = results;
        }
      }
    );
  }

  selectTags() {
    this.utils.showComponentDialog(
      TagsSelectorComponent,
      {
        selected: this.site.tag_ids,
        multiple: true},
      {
        width: '1200px'
      },(results: number[]) => {
        if (!results) { return; }
        this.site.tag_ids = results;
      }
    );
  }

  /**
   * Retrieves the details of the output start date and time based on the provided event.
   * @param {any} event - The event object containing the output date and time details.
   * @return {void}
   */
  getOutputStartDateTimeDetails(event: any) {
    this.estimatedProjectStartDate = event;
  }

  /**
   * Retrieves the details of the output end date and time based on the provided event.
   * @param {any} event - The event object containing the output date and time details.
   * @return {void}
   */
  getOutputEndDateTimeDetails(event: any) {
    this.estimatedProjectEndDate = event;
  }

  /**
   * Retrieves the details of the output site opening time based on the provided event.
   * @param {any} event - The event object containing the output time details.
   * @return {void}
   */
  getOutputOpeningTimeDetails(event: any) {
    this.siteOpenTime = event;
  }

  /**
   * Retrieves the details of the output site closing time based on the provided event.
   * @param {any} event - The event object containing the output time details.
   * @return {void}
   */
  getOutputClosingTimeDetails(event: any) {
    this.siteCloseTime = event;
  }

  /**
   * Convert the given time string in 'HH:mm:ss' format to a moment object with today's date as the carrier.
   *
   * @param {string | null} time - The time string in 'HH:mm:ss' format to convert to a moment object. Can be null.
   * @return {moment | null} A moment object with the time from the input string and today's date as the carrier, or null if the input time is invalid.
   */
  private toMoment(time: string | null) {
    if (!time) { return null; }
    // moment parses time; date portion defaults to today.
    return moment(time, 'HH:mm:ss', true).isValid()
      ? moment(time, 'HH:mm:ss')
      : null;
  }

  /**
   * Convert a moment object to a string in 'HH:mm:ss' format.
   *
   * @param {Moment | null} val - The moment object to be converted to a string. Can be null.
   * @return {string | null} A string representing the time in 'HH:mm:ss' format, or null if the input is null.
   */
  private toTimeString(val: Moment | null) {
    return val ? val.format('HH:mm:ss') : null;
  }

}
